"use client";
import React from 'react';
import { motion } from 'framer-motion';
import Link from 'next/link';

const sessionItems = [
    'Social Media Audit',
    'Audience & Content Analysis',
    'Competitor Review',
    'Brand Positioning Assessment',
    'Content Growth Roadmap',
];

export default function SocialCTA() {
    return (
        <section
            className="relative overflow-hidden py-28 px-6 md:px-16 lg:px-24"
            style={{ background: '#ffffff', borderTopLeftRadius: '72px', borderTopRightRadius: '72px' }}
        >
            {/* Dot grid */}
            <div className="absolute inset-0 pointer-events-none" style={{
                backgroundImage: 'radial-gradient(circle, rgba(55,48,163,0.07) 1.5px, transparent 1.5px)',
                backgroundSize: '32px 32px',
                maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
                WebkitMaskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
            }} />

            {/* Top glow */}
            <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 60% 40% at 50% 0%, rgba(55,48,163,0.06) 0%, transparent 70%)' }} />

            <div className="max-w-[1200px] mx-auto relative z-10">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">

                    {/* Left — text */}
                    <div className="flex flex-col gap-8">
                        <div className="flex flex-col gap-5">
                            <motion.span
                                initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                                className="text-xs font-bold tracking-[0.22em] uppercase"
                                style={{ color: '#3730a3', fontFamily: 'var(--font-clash), "Clash Display", sans-serif' }}
                            >
                                Get In Touch
                            </motion.span>
                            <motion.h2
                                initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.08 }}
                                style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 800, fontSize: 'clamp(34px, 4vw, 58px)', lineHeight: 1.0, letterSpacing: '-0.02em', color: '#080400' }}
                            >
                                Ready To Build A<br />Brand People<br />Want To Follow?
                            </motion.h2>
                        </div>

                        <motion.p
                            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.15 }}
                            className="text-base leading-relaxed"
                            style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(8,4,0,0.55)', letterSpacing: '0.02em', maxWidth: 460 }}
                        >
                            Whether you&apos;re looking to strengthen your presence, improve engagement, generate demand, or create a more consistent brand experience, we&apos;ll help you build a social media strategy rooted in human behaviour and business outcomes.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }}
                            className="flex flex-col sm:flex-row gap-4"
                        >
                            <Link
                                href="/contact"
                                className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full font-semibold transition-all duration-300 hover:scale-105 hover:shadow-[0_8px_32px_rgba(55,48,163,0.25)]"
                                style={{ background: '#3730a3', color: '#ffffff', fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 15, letterSpacing: '0.06em' }}
                            >
                                Book A Strategy Session
                                <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 7h10M7 2l5 5-5 5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
                            </Link>
                        </motion.div>
                    </div>

                    {/* Right — session items card */}
                    <motion.div
                        initial={{ opacity: 0, x: 32 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                        className="rounded-2xl overflow-hidden"
                        style={{ border: '1.5px solid rgba(55,48,163,0.14)', boxShadow: '0 24px 80px rgba(55,48,163,0.08)' }}
                    >
                        {/* Card header */}
                        <div className="px-8 py-6" style={{ background: '#3730a3' }}>
                            <p style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontWeight: 700, fontSize: 13, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.70)' }}>
                                Your Strategy Session Includes
                            </p>
                        </div>

                        {/* Items */}
                        <div className="flex flex-col divide-y divide-[rgba(55,48,163,0.08)]" style={{ background: '#ffffff' }}>
                            {sessionItems.map((item, i) => (
                                <motion.div
                                    key={i}
                                    initial={{ opacity: 0, x: 12 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ delay: 0.3 + i * 0.07 }}
                                    className="flex items-center gap-4 px-8 py-5"
                                    style={{ borderBottom: i < sessionItems.length - 1 ? '1px solid rgba(55,48,163,0.08)' : 'none' }}
                                >
                                    <div className="shrink-0 flex items-center justify-center rounded-full" style={{ width: 28, height: 28, background: 'rgba(55,48,163,0.07)', border: '1px solid rgba(55,48,163,0.18)' }}>
                                        <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                            <path d="M2 6l3 3 5-5" stroke="#3730a3" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                    </div>
                                    <span style={{ fontFamily: 'var(--font-manrope), Manrope, sans-serif', color: 'rgba(8,4,0,0.75)', fontSize: '0.9rem', fontWeight: 500 }}>
                                        {item}
                                    </span>
                                </motion.div>
                            ))}
                        </div>

                        {/* Card footer */}
                        <div className="px-8 py-5" style={{ background: 'rgba(55,48,163,0.04)', borderTop: '1px solid rgba(55,48,163,0.08)' }}>
                            <p style={{ fontFamily: 'var(--font-clash), "Clash Display", sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(8,4,0,0.25)' }}>
                                Build for visibility. Optimised for trust. Designed for growth.
                            </p>
                        </div>
                    </motion.div>

                </div>
            </div>
        </section>
    );
}
